import { create } from "zustand";
import type {
  AllgemeineDaten,
  Standort,
  Parkraum,
  Fuhrpark,
  Dienstwege,
  Arbeitsmodelle,
  Befragung,
  Plan,
} from "@/types";

interface AnalyseStore {
  // State
  plan: Plan;
  allgemeineDaten: AllgemeineDaten;
  standorte: Standort[];
  parkraum: Parkraum | null;
  fuhrpark: Fuhrpark | null;
  dienstwege: Dienstwege | null;
  arbeitsmodelle: Arbeitsmodelle | null;
  befragung: Befragung | null;
  vorhandeneMassnahmen: string[] | null;

  // Actions
  setPlan: (plan: Plan) => void;
  setAllgemeineDaten: (data: Partial<AllgemeineDaten>) => void;
  addStandort: (standort: Standort) => void;
  updateStandort: (index: number, standort: Partial<Standort>) => void;
  removeStandort: (index: number) => void;
  setParkraum: (data: Parkraum | null) => void;
  setFuhrpark: (data: Fuhrpark | null) => void;
  setDienstwege: (data: Dienstwege | null) => void;
  setArbeitsmodelle: (data: Arbeitsmodelle | null) => void;
  setBefragung: (data: Befragung | null) => void;
  setVorhandeneMassnahmen: (data: string[] | null) => void;
  reset: () => void;
}

/** Ergänzt Standorten ohne ID (vor Einführung gespeichert) eine feste ID. */
export function mitStandortIds(standorte: Standort[]): Standort[] {
  return standorte.map((s) => (s.id ? s : { ...s, id: crypto.randomUUID() }));
}

const initialAllgemeineDaten: AllgemeineDaten = {
  unternehmensname: "",
  branche: "",
  anzahlMitarbeitende: null,
  firmenkultur: "",
};

export const useAnalyseStore = create<AnalyseStore>()((set) => ({
  // Initialwerte
  plan: "basis",
  allgemeineDaten: initialAllgemeineDaten,
  standorte: [],
  parkraum: null,
  fuhrpark: null,
  dienstwege: null,
  arbeitsmodelle: null,
  befragung: null,
  vorhandeneMassnahmen: null,

  // Actions
  setPlan: (plan) => set({ plan }),

  setAllgemeineDaten: (data) =>
    set((state) => ({
      allgemeineDaten: { ...state.allgemeineDaten, ...data },
    })),

  addStandort: (standort) =>
    set((state) => ({
      standorte: [...state.standorte, standort.id ? standort : { ...standort, id: crypto.randomUUID() }],
    })),

  updateStandort: (index, standort) =>
    set((state) => ({
      standorte: state.standorte.map((s, i) =>
        i === index ? { ...s, ...standort } : s
      ),
    })),

  removeStandort: (index) =>
    set((state) => ({
      standorte: state.standorte.filter((_, i) => i !== index),
    })),

  setParkraum: (data) => set({ parkraum: data }),
  setFuhrpark: (data) => set({ fuhrpark: data }),
  setDienstwege: (data) => set({ dienstwege: data }),
  setArbeitsmodelle: (data) => set({ arbeitsmodelle: data }),
  setBefragung: (data) => set({ befragung: data }),
  setVorhandeneMassnahmen: (data) => set({ vorhandeneMassnahmen: data }),

  reset: () =>
    set({
      plan: "basis",
      allgemeineDaten: initialAllgemeineDaten,
      standorte: [],
      parkraum: null,
      fuhrpark: null,
      dienstwege: null,
      arbeitsmodelle: null,
      befragung: null,
      vorhandeneMassnahmen: null,
    }),
}));
